import {
	Children,
	CSSProperties,
	PropsWithChildren,
	ReactNode,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { css, keyframes } from '@compiled/react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';

import { isReducedMotion } from '@atlaskit/motion';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { GalleryControls } from '@/libraries/design/components/gallery-controls/src/gallery-controls';
import { ColumnLayout, wacToken } from '@/libraries/design/foundations';
import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_MAX_WIDTH_XL,
	WCL_GRID_PADDING_LG,
	WCL_GRID_PADDING_SM,
	WCL_GRID_PADDING_XL,
} from '@/libraries/design/foundations/grid/src/base';

export interface GalleryContainerProps extends PropsWithChildren {
	// Additional motion within the scrolling container can be disabled
	isMotionAvailable?: boolean;
	motionDelay?: string;
	// Gap between gallery items in pixels. Defaults to space.400 (16px) below md and space.600 (24px) above md.
	gap?: { aboveMd: string; belowMd: string };
	showEntry?: boolean;
}

// All CSS Custom Properties prefixed --gc-scroll are to power scroll driven animations on sub elements
// Additional necessary @property rules appear in root index.css file
// Based on: https://www.bram.us/2023/10/23/css-scroll-detection/
const scrollAnimations = keyframes({
	'100%': {
		'--gc-scroll-position': `1`,
		'--gc-scroll-position-delayed': `1`,
	},
});

const galleryStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'end',
	gap: token('space.400'),
	[media.above.md]: {
		gap: token('space.500'),
	},
});

const scrollableStyles = css({
	// Aligns to the grid but bleeds beyond the end, inline direction
	width: '100%',
	overflowY: 'hidden',
	overflowX: 'auto',
	['@supports (scrollbar-width: none)']: {
		scrollbarWidth: 'none',
	},
	paddingBlockStart: token('space.300'),
	scrollBehavior: 'smooth',
	scrollSnapType: 'x mandatory',
	paddingInline: `var(--cl-overflow-bleed, 0px)`,
	scrollPaddingInline: `var(--cl-overflow-bleed, 0px)`,
	'--cl-overflow-bleed': `${WCL_GRID_PADDING_SM}px`,
	[media.only.sm]: {
		'--cl-overflow-bleed': `${WCL_GRID_PADDING_LG}px`,
	},
	[media.only.md]: {
		'--cl-overflow-bleed': `max(${WCL_GRID_PADDING_LG}px, calc((100% - ${
			WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2
		}px) / 2 + ${WCL_GRID_PADDING_LG}px))`,
	},
	[media.above.lg]: {
		'--cl-overflow-bleed': `max(${WCL_GRID_PADDING_XL}px, calc((100% - ${
			WCL_GRID_MAX_WIDTH_XL + WCL_GRID_PADDING_XL * 2
		}px) / 2 + ${WCL_GRID_PADDING_XL}px))`,
	},
});

const scrollableAnimation = css({
	'@media (prefers-reduced-motion: no-preference)': {
		animationName: scrollAnimations,
		animationTimingFunction: 'linear',
		animationFillMode: 'both',
		animationTimeline: 'scroll(x self)',
		overscrollBehaviorInline: 'contain',
	},
});

// Applied when all content fits within the viewport — centers the inner row and removes bleed/scroll behaviour
const scrollableCenteredStyles = css({
	display: 'flex',
	justifyContent: 'center',
	paddingInline: 0,
	scrollPaddingInline: 0,
});

const innerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'start',
	width: 'max-content',
	'--wcl-gallery-gap': `var(--gc-gap-below-md, ${token('space.400')})`,
	gap: 'var(--wcl-gallery-gap)',
	[media.above.md]: {
		'--wcl-gallery-gap': `var(--gc-gap-above-md, ${token('space.600')})`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		scrollSnapAlign: 'start',
		[media.below.sm]: {
			scrollSnapAlign: 'center',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > span': {
		'&:first-of-type': {
			marginInlineEnd: `calc(var(--wcl-gallery-gap) * -1)`,
		},
		'&:last-of-type': {
			marginInlineStart: `calc(var(--wcl-gallery-gap) * -1)`,
		},
	},

	// motion setup for scrolling
	transition: '--gc-scroll-position-delayed 166.667ms linear',
	'--gc-scroll-velocity': 'calc(var(--gc-scroll-position) - var(--gc-scroll-position-delayed))',
	'--gc-scroll-speed': 'max(var(--gc-scroll-velocity), -1 * var(--gc-scroll-velocity))',
	'--gc-scroll-direction': 'calc(var(--gc-scroll-velocity) / var(--gc-scroll-speed))',
});

const entryFrames = keyframes({
	from: {
		transform: 'translateX(1400px)',
		opacity: 0,
	},
});
const entrySetup = css({
	'@media (prefers-reduced-motion: no-preference)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.entry-item': {
			transform: 'translateX(0)',
			opacity: 1,
			animationName: entryFrames,
			animationDuration: '750ms',
			animationTimingFunction: wacToken('motion.easing.ease40-out'),
			animationDelay: 'calc(var(--gc-item-i, 0) * 66.667ms)',
			animationFillMode: 'backwards',
			animationPlayState: 'paused',
		},
	},
});
const entryAnimation = css({
	'@media (prefers-reduced-motion: no-preference)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.entry-item': {
			animationPlayState: 'running',
		},
	},
});

const controlsStyles = css({
	transition: 'opacity 75ms linear',
	width: '100%',
});
const controlsHiddenStyles = css({
	opacity: 0,
});

const paginationStyles = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.100'),
	width: '100%',
	paddingBlockStart: token('space.100'),
	listStyle: 'none',
	margin: 0,
	paddingInline: 0,
});

const paginationDotStyles = css({
	appearance: 'none',
	border: 'none',
	padding: 0,
	width: '8px',
	height: '8px',
	borderRadius: '100%',
	backgroundColor: wacToken('color.neutral.light700'),
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${token('color.border.focused')}`,
		outlineOffset: '2px',
	},
});

const paginationDotActiveStyles = css({
	backgroundColor: wacToken('color.primary.black'),
});

const SCROLL_BUFFER = 40;

/** Items sit between the leading/trailing aria-hidden marker spans. */
const getGalleryItems = (innerEl: HTMLDivElement) =>
	Array.from(innerEl.children).slice(1, -1) as HTMLElement[];

/** Mobile-only (< sm / 48rem). Tablet+ uses arrow controls. */
const useIsBelowSm = () => {
	const [isBelowSm, setIsBelowSm] = useState(false);

	useEffect(() => {
		const mq = window.matchMedia('(max-width: 47.99rem)');
		const sync = () => setIsBelowSm(mq.matches);
		sync();
		mq.addEventListener('change', sync);
		return () => mq.removeEventListener('change', sync);
	}, []);

	return isBelowSm;
};

const useGalleryFitting = (
	scrollRef: RefObject<HTMLDivElement>,
	innerRef: RefObject<HTMLDivElement>,
) => {
	const [isFitting, setIsFitting] = useState(false);

	useEffect(() => {
		const scrollEl = scrollRef.current;
		const innerEl = innerRef.current;
		if (!scrollEl || !innerEl) {
			return;
		}

		const observer = new ResizeObserver(() => {
			setIsFitting(innerEl.scrollWidth <= scrollEl.clientWidth - WCL_GRID_PADDING_SM * 2);
		});
		observer.observe(scrollEl);
		observer.observe(innerEl);
		return () => observer.disconnect();
	}, [scrollRef, innerRef]);

	return isFitting;
};

/** Tracks the centered slide for mobile pagination dots. No-ops when disabled. */
const useActiveGalleryIndex = (
	scrollRef: RefObject<HTMLDivElement>,
	innerRef: RefObject<HTMLDivElement>,
	enabled: boolean,
) => {
	const [activeIndex, setActiveIndex] = useState(0);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const scrollEl = scrollRef.current;
		const innerEl = innerRef.current;
		if (!scrollEl || !innerEl) {
			return;
		}

		const update = () => {
			const items = getGalleryItems(innerEl);
			if (items.length === 0) {
				return;
			}
			const viewCenter = scrollEl.getBoundingClientRect().left + scrollEl.clientWidth / 2;
			let closest = 0;
			let closestDist = Infinity;
			items.forEach((item, index) => {
				const rect = item.getBoundingClientRect();
				const dist = Math.abs((rect.left + rect.right) / 2 - viewCenter);
				if (dist < closestDist) {
					closestDist = dist;
					closest = index;
				}
			});
			setActiveIndex(closest);
		};

		update();
		scrollEl.addEventListener('scroll', update, { passive: true });
		const observer = new ResizeObserver(update);
		observer.observe(scrollEl);
		observer.observe(innerEl);
		return () => {
			scrollEl.removeEventListener('scroll', update);
			observer.disconnect();
		};
	}, [enabled, scrollRef, innerRef]);

	const scrollToIndex = useCallback(
		(index: number) => {
			const innerEl = innerRef.current;
			if (!innerEl) {
				return;
			}
			getGalleryItems(innerEl)[index]?.scrollIntoView({
				inline: 'center',
				block: 'nearest',
				behavior: isReducedMotion() ? 'auto' : 'smooth',
			});
		},
		[innerRef],
	);

	return { activeIndex, scrollToIndex };
};

const GalleryNavControls = ({
	isStartVisible,
	isEndVisible,
	onPrevious,
	onNext,
}: {
	isStartVisible: boolean;
	isEndVisible: boolean;
	onPrevious: () => void;
	onNext: () => void;
}) => (
	<ColumnLayout layout="12" horizontalAlignment="end">
		<div css={[controlsStyles, isStartVisible && isEndVisible && controlsHiddenStyles]}>
			<GalleryControls
				isFirstItemVisible={isStartVisible}
				isLastItemVisible={isEndVisible}
				onPrevious={onPrevious}
				onNext={onNext}
				size="large"
			/>
		</div>
	</ColumnLayout>
);

const GalleryPaginationDots = ({
	itemCount,
	activeIndex,
	onSelect,
}: {
	itemCount: number;
	activeIndex: number;
	onSelect: (index: number) => void;
}) => {
	const intl = useIntl();

	if (itemCount < 2) {
		return null;
	}

	return (
		<ul
			css={paginationStyles}
			aria-label={intl.formatMessage({
				id: 'gallery-container.pagination-label',
				defaultMessage: 'Gallery slides',
			})}
			data-testid="gallery-pagination-dots"
		>
			{Array.from({ length: itemCount }, (_, index) => (
				<li key={index}>
					<button
						type="button"
						aria-current={index === activeIndex ? 'true' : undefined}
						aria-label={intl.formatMessage(
							{
								id: 'gallery-container.pagination-dot',
								defaultMessage: 'Go to slide {slide}',
							},
							{ slide: index + 1 },
						)}
						css={[paginationDotStyles, index === activeIndex && paginationDotActiveStyles]}
						onClick={() => onSelect(index)}
						data-testid={`gallery-pagination-dot-${index}`}
					/>
				</li>
			))}
		</ul>
	);
};

const GalleryScrollTrack = ({
	scrollRef,
	innerRef,
	firstItemRef,
	lastItemRef,
	isMotionAvailable,
	isFitting,
	motionDelay,
	showEntry,
	isGalleryInView,
	children,
}: {
	scrollRef: RefObject<HTMLDivElement>;
	innerRef: RefObject<HTMLDivElement>;
	firstItemRef: (node?: Element | null) => void;
	lastItemRef: (node?: Element | null) => void;
	isMotionAvailable?: boolean;
	isFitting: boolean;
	motionDelay?: string;
	showEntry: boolean;
	isGalleryInView: boolean;
	children: ReactNode;
}) => (
	<div
		css={[
			scrollableStyles,
			isMotionAvailable && scrollableAnimation,
			isFitting && scrollableCenteredStyles,
		]}
		ref={scrollRef}
	>
		<div
			css={[
				innerStyles,
				motionDelay && css({ transitionDuration: motionDelay }),
				showEntry && entrySetup,
				showEntry && isGalleryInView && entryAnimation,
			]}
			ref={innerRef}
		>
			<span aria-hidden="true" ref={firstItemRef} />
			{children}
			<span aria-hidden="true" ref={lastItemRef} />
		</div>
	</div>
);

export const GalleryContainer = ({
	isMotionAvailable,
	motionDelay,
	gap,
	showEntry = true,
	children,
}: GalleryContainerProps) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const innerRef = useRef<HTMLDivElement>(null);
	const isFitting = useGalleryFitting(scrollRef, innerRef);
	const isBelowSm = useIsBelowSm();
	const itemCount = Children.toArray(children).length;
	const showDots = isBelowSm && !isFitting && itemCount > 1;
	const showArrows = !isBelowSm && !isFitting;
	const { activeIndex, scrollToIndex } = useActiveGalleryIndex(scrollRef, innerRef, showDots);

	const { ref: firstItemRef, inView: isStartVisible } = useInView({
		root: scrollRef?.current,
		threshold: 1,
	});
	const { ref: lastItemRef, inView: isEndVisible } = useInView({
		root: scrollRef?.current,
		threshold: 1,
	});

	const handleChange = useCallback((direction: number) => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		const pageAmount = Math.min(WCL_GRID_MAX_WIDTH, el.clientWidth);
		el.scrollTo({
			top: 0,
			left: el.scrollLeft + (pageAmount - SCROLL_BUFFER) * direction,
			behavior: isReducedMotion() ? 'auto' : 'smooth',
		});
	}, []);

	const handlePrevious = useCallback(() => handleChange(-1), [handleChange]);
	const handleNext = useCallback(() => handleChange(1), [handleChange]);

	const { ref: containerRef, inView: isGalleryInView } = useInView({
		threshold: 0.25,
		triggerOnce: true,
	});

	const gapStyle = gap
		? ({
				'--gc-gap-below-md': gap.belowMd,
				'--gc-gap-above-md': gap.aboveMd,
			} as CSSProperties)
		: undefined;

	return (
		<div className="gallery-container" css={galleryStyles} style={gapStyle} ref={containerRef}>
			{showArrows && (
				<GalleryNavControls
					isStartVisible={isStartVisible}
					isEndVisible={isEndVisible}
					onPrevious={handlePrevious}
					onNext={handleNext}
				/>
			)}
			<GalleryScrollTrack
				scrollRef={scrollRef}
				innerRef={innerRef}
				firstItemRef={firstItemRef}
				lastItemRef={lastItemRef}
				isMotionAvailable={isMotionAvailable}
				isFitting={isFitting}
				motionDelay={motionDelay}
				showEntry={showEntry}
				isGalleryInView={isGalleryInView}
			>
				{children}
			</GalleryScrollTrack>
			{showDots && (
				<GalleryPaginationDots
					itemCount={itemCount}
					activeIndex={activeIndex}
					onSelect={scrollToIndex}
				/>
			)}
		</div>
	);
};
